import {
	DateString,
	DialogBackdropState,
	FormattedText,
	Icon,
	InlineOptionSelectorItem,
	OptionsMenu,
	Pagination,
	SearchInputForm,
	SelectItem,
	TableView,
	TableViewNoResultConfig,
	TagList,
	ToolbarAction,
	ToolbarRegularAction,
	Typography,
	useConfirmationDialog
} from "client/Components";
import { contains, normalizeForSearch } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { FilterForm } from "client/Features/Settings/FilterForm/FilterForm";
import {
	createLocalDataSource,
	useDeepComparison,
	useSelection,
	useSortHandler,
	useUserTimezone,
	useFilterHandler,
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import {
	isNil,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	archiveKnowledgeItems,
	deleteKnowledgeItems,
	KnowledgeItemListItemModel,
	KnowledgeItemListModel,
	KnowledgeItemStatus,
	listKnowledgeItems
} from "client/Api/KnowledgeItemManagement/knowledgeItemManagement";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubManagementDialogContentLayout } from "../../HubManagementDialogContentLayout";
import { useEditKnowledgeItemDialog } from "../../../KnowledgeItemsManagement/EditKnowledgeItemDialog/EditKnowledgeItemDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useCreateHubTemplateDialog } from "../../../Templates/CreateHubTemplateDialog";
import { useCreateHubItemDialog } from "client/Features/Hub/CreateHubItemDialog/CreateHubItemDialog";

export type KnowledgeItemsOverviewProps = {
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
	isMobile: boolean;
	isResponsive: boolean;
	onItemsChanged: () => void;
};

export const KnowledgeItemsOverview = (props: KnowledgeItemsOverviewProps): ReactElement => {
	const {
		onParentBackdropChange,
		isMobile,
		isResponsive,
		onItemsChanged
	} = props;

	const [knowledgeItemsList, setKnowledgeItemsList] = useState<KnowledgeItemListModel>({
		knowledgeItems: [],
		categories: [],
		canCreateTemplates: false
	});

	const [selectedKnowledgeItems, setSelectedKnowledgeItems] = useState<readonly KnowledgeItemListItemModel[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const [filter, setFilter] = useState<FilterState>({
		title: "",
		activeCategoryId: null,
		createdBy: "",
		modifiedBy: "",
		status: null
	});

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<KnowledgeItemListItemModel>>({
		direction: DataSourceSortDirection.Descending,
		propertyName: "publicationDate"
	});

	const alertManager: AlertManager = useAlerts();
	const [reload, dataVersion] = useForceRerender();
	const [editKnowledgeItemDialog, openEditKnowledgeItemDialog] = useEditKnowledgeItemDialog();
	const [createHubItemDialog, openCreateHubItemDialog] = useCreateHubItemDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [createHubTemplateDialog, openCreateHubTemplateDialog] = useCreateHubTemplateDialog();

	const editOrCreateKnowledgeItemHandler = useCallback(
		async (knowledgeItem?: KnowledgeItemListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");
			let result: TryResult<void>;

			if (isNil(knowledgeItem?.id)) {
				result = await openCreateHubItemDialog({
					kind: HubItemKind.KnowledgeItem
				});
			} else {
				result = await openEditKnowledgeItemDialog({
					mode: {
						kind: "edit",
						itemId: knowledgeItem?.id
					}
				});
			}

			onParentBackdropChange("visible");
			if (result.wasSuccessful) {
				reload();
				onItemsChanged();
			}
		},
		[
			onParentBackdropChange,
			openEditKnowledgeItemDialog,
			reload,
			openCreateHubItemDialog,
			onItemsChanged
		]
	);

	const deleteKnowledgeItemsHandler = useCallback(
		async (knowledgeItems: readonly KnowledgeItemListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToDeleteThisItem(s)"),
					confirmText: localize("_Delete"),
					trackingId:"knowledge-item-delete-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				await deleteKnowledgeItems(knowledgeItems.map(a => a.id));
				setSelectedKnowledgeItems([]);
				setIsBusy(false);
				reload();
				onItemsChanged();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			openConfirmationDialog,
			reload,
			onItemsChanged
		]
	);

	const archiveKnowledgeItemsHandler = useCallback(
		async (knowledgeItems: readonly KnowledgeItemListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToArchiveThisItem(s)"),
					confirmText: localize("_Archive"),
					trackingId:"knowledge-item-archive-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				await archiveKnowledgeItems(knowledgeItems.map(a => a.id));
				setIsBusy(false);
				reload();
				onItemsChanged();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			openConfirmationDialog,
			reload,
			onItemsChanged
		]
	);

	const saveAsTemplateHandler = useCallback(
		async (item: KnowledgeItemListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			await openCreateHubTemplateDialog({
				kind: HubItemKind.KnowledgeItem,
				basedOn: {
					hubItemId: item.id
				}
			});

			onParentBackdropChange("visible");
		},
		[
			onParentBackdropChange,
			openCreateHubTemplateDialog
		]
	);

	const duplicateHandler = useCallback(
		async (item: KnowledgeItemListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			const { wasSuccessful } = await openEditKnowledgeItemDialog({
				mode: {
					kind: "duplicate",
					itemToCopyId: item.id
				}
			});

			if (wasSuccessful) {
				reload();
				onItemsChanged();
			}

			onParentBackdropChange("visible");
		},
		[
			onItemsChanged,
			onParentBackdropChange,
			openEditKnowledgeItemDialog,
			reload
		]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const knowledgeItemsList: KnowledgeItemListModel = await listKnowledgeItems(signal);
					setKnowledgeItemsList(knowledgeItemsList);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, dataVersion]
	);

	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	const {
		filteringFor
	} = useFilterHandler<FilterState>(filter, setFilter);

	const itemsModifiersVersion: number = useDeepComparison([sortingState, searchTerm, filter]);
	const userTimezone: TimeZone = useUserTimezone();

	useEffect(
		() => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	const categoryIdOptions = useMemo(
		(): readonly InlineOptionSelectorItem<number | null>[] => {
			return [
				{
					text: localize("_AllKnowledgeArticles"),
					value: null,
					valueTrackingId: "all-knowledge-items"
				},
				...knowledgeItemsList.categories.map(c => ({
					text: c.name,
					value: c.id
				}))
			];
		},
		[knowledgeItemsList.categories]
	);

	const {
		currentItems,
		pagination
	} = useKnowledgeItemsDataSource(
		knowledgeItemsList.knowledgeItems,
		{
			searchTerm,
			filtering: filter,
			pagination: {
				currentPageNumber
			},
			sorting: sortingState
		}
	);

	const {
		selectionFor,
		multiSelection
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selectedKnowledgeItems,
			setSelectedItems: setSelectedKnowledgeItems
		}
	);

	const actionContext = useMemo(
		(): KnowledgeItemActionContext => {
			return {
				selectedKnowledgeItems,
				canCreateTemplates: knowledgeItemsList.canCreateTemplates,
				isMobile
			};
		},
		[
			knowledgeItemsList.canCreateTemplates,
			isMobile,
			selectedKnowledgeItems
		]
	);

	const selectionActions = useMemo(
		(): readonly KnowledgeItemAction[] => {
			return getSelectionActions(
				() => setSelectedKnowledgeItems([])
			);
		},
		[]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<KnowledgeItemActionContext> => {
			return getMainAction(
				editOrCreateKnowledgeItemHandler
			);
		},
		[editOrCreateKnowledgeItemHandler]
	);

	const contextualActions = useMemo(
		(): readonly KnowledgeItemAction[] => {
			return getContextualActions(
				editOrCreateKnowledgeItemHandler,
				deleteKnowledgeItemsHandler,
				archiveKnowledgeItemsHandler,
				saveAsTemplateHandler,
				duplicateHandler
			);
		},
		[
			archiveKnowledgeItemsHandler,
			deleteKnowledgeItemsHandler,
			duplicateHandler,
			editOrCreateKnowledgeItemHandler,
			saveAsTemplateHandler
		]
	);

	const hasSelection: boolean = selectedKnowledgeItems.length > 0;

	const actions = useMemo(
		(): readonly KnowledgeItemAction[] => {
			return buildActionsForTopBar<KnowledgeItemActionContext, KnowledgeItemAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			const message: string = knowledgeItemsList.knowledgeItems.length === 0
				? localize("_ThereAreNoKnowledgeArticlesYet")
				: isNotNilOrEmpty(searchTerm)
					? localize("_NoKnowledgeArticlesMatchedYourSearchKeywords")
					: localize("_NoKnowledgeArticlesInThisCategory");

			if (isNotNilOrEmpty(searchTerm)) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message
				};
			}

			return {
				icon: "book",
				title: localize("_NoKnowledgeArticlesYet"),
				message
			};
		},
		[
			searchTerm,
			knowledgeItemsList.knowledgeItems
				.length
		]
	);

	return (
		<HubManagementDialogContentLayout
			title={localize("_KnowledgeArticles")}
			isMobile={isMobile}
			isResponsive={isResponsive}
			actions={actions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			trackingId="knowledge-items-overview"
			isBusy={isBusy}
		>
			<HubManagementDialogContentLayout.Filter>
				<SearchInputForm
					onChange={setSearchTerm}
					trackingId="knowledge-item-search-form"
					placeholder={
						<FormattedText
							text={localize("_FilterByProperties")}
							thingsToReplace={[
								{
									what: "{{Properties}}",
									filler: (
										<Typography
											weight="bold"
											isItalic
										>
											{localize("_Title")}
										</Typography>
									)
								}
							]}
						/>
					}
					height="large"
					icon="filter"
				/>
				<OptionsMenu
					value={filteringFor("activeCategoryId").value}
					onChange={filteringFor("activeCategoryId").setValue}
					options={categoryIdOptions}
					trackingId="knowledge-item-category-selector"
					icon="filter"
					title={localize("_Category")}
					buttonSize="large"
				/>
			</HubManagementDialogContentLayout.Filter>
			<HubManagementDialogContentLayout.Table>
				<TableView
					trackingId="knowledge-item-grid"
					multiSelectable={multiSelection}
					items={currentItems}
					height="stretch"
					itemKey="id"
					columns={[
					{
						title: localize("_Title"),
						width: 270,
						sortable: sortingFor("title"),
						filterable: filteringFor("title"),
						trackingId: "title"
					},
					{
						title: localize("_PublicationDate"),
						width: 220,
						sortable: sortingFor("publicationDate"),
						trackingId: "publication-date"
					},
					{
						title: localize("_DateModified"),
						width: 220,
						sortable: sortingFor("lastModifiedDate"),
						trackingId: "modified-date"
					},
					{
						title: localize("_Status"),
						width: 150,
						sortable: sortingFor("status"),
						filterable: {
							columnFilterableFormFactory: (onClose) => (
								<FilterForm
									filterable={filteringFor("status")}
									onClose={onClose}
									statusFilterOptions={statusFilterOptions}
								/>
							),
							isActive: isNotNil(filter.status)
						},
						trackingId: "status"
					},
					{
						title: localize("_CreatedBy"),
						width: 160,
						sortable: sortingFor("createdByUserName"),
						filterable: filteringFor("createdBy"),
						trackingId: "created-by"
					},
					{
						title: localize("_LastModifiedBy"),
						width: 160,
						sortable: sortingFor("lastModifiedByUserName"),
						filterable: filteringFor("modifiedBy"),
						trackingId: "last-modified-by"
					},
					{
						title: localize("_Comments"),
						width: 90,
						sortable: sortingFor("commentsCount"),
						trackingId: "comments-count"
					},
					{
						title: localize("_Reactions"),
						width: 90,
						sortable: sortingFor("reactionsCount"),
						trackingId: "likes-count"
					},
					{
						title: localize("_Views"),
						width: 90,
						sortable: sortingFor("viewsCount"),
						trackingId: "views-count"
					},
					{
						title: localize("_Category"),
						width: 90,
						sortable: sortingFor("categoryName"),
						trackingId: "category-name"
					},
					{
						title: localize("_Pinned"),
						width: 90,
						sortable: sortingFor("isPinned"),
						trackingId: "pinned"
					},
					{
						title: localize("_Tags"),
						width: 200,
						trackingId: "tags",
						isExpensiveToRender: true
					}
					]}
					noResultConfig={noResultConfig}
				>
					{k => (
						<TableView.Row
							selectable={selectionFor(k)}
							onExecute={() => editOrCreateKnowledgeItemHandler(k)}
							trackingId={k.id.toString()}
						>
							<TableView.Cell>
								{k.title}
							</TableView.Cell>
							<TableView.Cell>
								{isNotNil(k.publicationDate) && (
									<DateString
										value={k.publicationDate}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)}
							</TableView.Cell>
							<TableView.Cell>
								<DateString
									value={k.lastModifiedDate}
									format="RelativeDayWithTime"
									timezone={userTimezone}
								/>
							</TableView.Cell>
							<TableView.Cell>
								{KnowledgeItemStatusToLocalizationKeyMap[k.status]}
							</TableView.Cell>
							<TableView.Cell>
								{k.createdByUserName}
							</TableView.Cell>
							<TableView.Cell>
								{k.lastModifiedByUserName}
							</TableView.Cell>
							<TableView.Cell>
								{k.commentsCount}
							</TableView.Cell>
							<TableView.Cell>
								{k.reactionsCount}
							</TableView.Cell>
							<TableView.Cell>
								{k.viewsCount}
							</TableView.Cell>
							<TableView.Cell>
								{k.categoryName}
							</TableView.Cell>
							<TableView.Cell>
								{k.isPinned &&
									<Icon value="check" size="large" />
								}
							</TableView.Cell>
							<TableView.Cell>
								<TagList
									trackingId="knowledge-item-tag-list"
									showMoreTagsColor="secondary"
									tags={
										k.tags
											.map(t => ({
												value: t.id,
												text: t.name,
												color: "secondary"
											}))
									}
								/>
							</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
				<Pagination
					{...pagination}
					onPageNumberChanged={setCurrentPageNumber}
					trackingId="KnowledgeItems-pagination"
				/>
			</HubManagementDialogContentLayout.Table>
			{isResponsive && mainAction.canExecute(actionContext) && (
				<HubManagementDialogContentLayout.MainButton
					onClick={() => mainAction.execute(actionContext)}
					trackingId={mainAction.name}
				/>
			)}
			{confirmationDialog}
			{editKnowledgeItemDialog}
			{createHubTemplateDialog}
			{createHubItemDialog}
		</HubManagementDialogContentLayout>
	);
};
KnowledgeItemsOverview.displayName="KnowledgeItemsOverview";

type FilterState = {
	title: string;
	activeCategoryId: number | null;
	status: KnowledgeItemStatus | null;
	createdBy: string;
	modifiedBy: string;
};

function applyFilter(
	items: readonly KnowledgeItemListItemModel[],
	filter: FilterState
): readonly KnowledgeItemListItemModel[] {
	const normalizedTitle: string = normalizeForSearch(filter.title);
	const normalizedCreatedByUserName: string = normalizeForSearch(filter.createdBy);
	const normalizedLastModifiedByUserName: string = normalizeForSearch(filter.modifiedBy);

	return items.filter(i =>
		(filter.activeCategoryId === null || i.categoryId === filter.activeCategoryId)
			&& (filter.status === null || i.status === filter.status)
			&& (
				normalizedCreatedByUserName === ""
					|| contains(i.createdByUserName, normalizedCreatedByUserName)
			)
			&& (
				normalizedLastModifiedByUserName === ""
					|| contains(i.lastModifiedByUserName, normalizedLastModifiedByUserName)
			)
			&& (normalizedTitle === "" || contains(i.title, normalizedTitle))
	);
}

const useKnowledgeItemsDataSource = createLocalDataSource<KnowledgeItemListItemModel, FilterState>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.title, searchTerm)
		},
		filtering: { applyFilter },
		hasPagination: true
	})
	.getHook();

const statusFilterOptions: readonly SelectItem<KnowledgeItemStatus>[] = [
	{
		value: KnowledgeItemStatus.Draft,
		text: localize("_Draft")
	},
	{
		value: KnowledgeItemStatus.Published,
		text: localize("_Published")
	},
	{
		value: KnowledgeItemStatus.Archived,
		text: localize("_Archived")
	}
];

export const KnowledgeItemStatusToLocalizationKeyMap: {
	[key in KnowledgeItemStatus]: string;
} = {
	[KnowledgeItemStatus.Published]: localize("_Published"),
	[KnowledgeItemStatus.Archived]: localize("_Archived"),
	[KnowledgeItemStatus.Draft]: localize("_Draft")
};

type KnowledgeItemActionContext = {
	selectedKnowledgeItems: readonly KnowledgeItemListItemModel[];
	canCreateTemplates: boolean;
	isMobile: boolean;
};

type KnowledgeItemAction = ToolbarAction<KnowledgeItemActionContext>;

function getSelectionActions(
	deselectHandler: () => void
): readonly KnowledgeItemAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedKnowledgeItems),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedKnowledgeItems.length > 0,
			execute: () => deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedKnowledgeItems.length > 0
		}
	];
}

function getMainAction(
	newKnowledgeItemHandler: () => void
): ToolbarRegularAction<KnowledgeItemActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNewKnowledgeItem",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newKnowledgeItemHandler(),
		icon: "plus",
		isPrimary: true
	};
}

function getContextualActions(
	editKnowledgeItemHandler: (item: KnowledgeItemListItemModel) => void,
	deleteKnowledgeItemsHandler: (item: readonly KnowledgeItemListItemModel[]) => void,
	archiveKnowledgeItemsHandler: (item: readonly KnowledgeItemListItemModel[]) => void,
	saveAsTemplateHandler: (item: KnowledgeItemListItemModel) => void,
	duplicateKnowledgeItemHandler: (item: KnowledgeItemListItemModel) => void
): readonly KnowledgeItemAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "editKnowledgeItem",
			title: localize("_Edit"),
			canExecute: (c) => c.selectedKnowledgeItems.length === 1,
			execute: (c) => editKnowledgeItemHandler(c.selectedKnowledgeItems[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "deleteKnowledgeItems",
			title: localize("_Delete"),
			canExecute: (c) => c.selectedKnowledgeItems.length > 0,
			execute: (c) => deleteKnowledgeItemsHandler(c.selectedKnowledgeItems),
			icon: "trash-can"
		},
		{
			kind: ActionKind.Regular,
			name: "archiveKnowledgeItems",
			title: localize("_Archive"),
			canExecute: (c) => c.selectedKnowledgeItems
				.some(x => x.status !== KnowledgeItemStatus.Archived),
			execute: (c) => archiveKnowledgeItemsHandler(c.selectedKnowledgeItems),
			icon: "box-archive"
		},
		{
			kind: ActionKind.Regular,
			name: "duplicateKnowledgeItem",
			title: localize("_Duplicate"),
			canExecute: c => c.selectedKnowledgeItems.length === 1,
			execute: c => duplicateKnowledgeItemHandler(c.selectedKnowledgeItems[0]),
			icon: "copy"
		},
		{
			kind: ActionKind.Regular,
			name: "saveAsTemplate",
			title: localize("_SaveAsTemplate"),
			canExecute: (c) => c.canCreateTemplates
				&& c.selectedKnowledgeItems.length === 1,
			execute: (c) => saveAsTemplateHandler(c.selectedKnowledgeItems[0]),
			icon: "floppy-disk"
		}
	];
}
